// Where a scenario starts the ship.
//
// OWNER DECISION 2: the only thing a scenario places is the SHIP, and it places
// it as an ORBIT, not as a point - canon's `phase-h/03-land/
// 00-universal-land-plan.md` III.5 licenses free space by orbital element and
// reference-frame id rather than by hex, and this is that record. The combat
// bubble is then derived from wherever the ship's own orbit puts it, so there
// is no fight location to carry and none of these rows names one.
//
// The default is a high Mars orbit: Mars a bright disc off the bow rather than
// a wall, with low orbit somewhere the player can fly to. A run selects another
// with `?shiporbit=<id>`; nothing but that query parameter reaches the shipped
// default, which is why `pluto-high` can be a row here without being a place
// the game ever starts.

// These imports carry their real extension: `tools/preview/descent-dojo.mjs`
// reads this table under Node's type stripping to compute the state the ship
// should be in, the way `tools/preview/ephem-check.mjs` reads the propagator.
import { J2000_JD } from '../world/clock.ts';
import { DAYS_PER_CENTURY } from '../world/ephemeris.ts';
import type { BodyRow, Orbit } from '../world/types.ts';
import { GRAVITATIONAL_CONSTANT_KM3_KG_S2 as G_KM3_KG_S2 } from './constants.ts';

const AU_KM = 149597870.7;
const SECONDS_PER_DAY = 86400;

/**
 * One licensed orbit: the six elements plus the reference frame the parent's
 * id names. Angles are degrees in the ecliptic J2000 frame, `a_km` is the
 * semi-major axis about the parent's centre.
 */
export interface ShipOrbitSpec {
  readonly id: string;
  readonly label: string;
  /** Registry row id the elements are relative to. */
  readonly parent: string;
  readonly a_km: number;
  readonly e: number;
  readonly i_deg: number;
  readonly long_node_deg: number;
  readonly arg_peri_deg: number;
  readonly mean_anomaly_deg: number;
}

export const DEFAULT_SHIP_ORBIT = 'mars-high';

export const SHIP_ORBITS: readonly ShipOrbitSpec[] = [
  {
    id: 'mars-high',
    label: 'high Mars orbit',
    parent: 'mars',
    // Areostationary: one Mars sidereal day, 20,428 km, so the disc holds
    // about 19 degrees off the bow and low orbit is a trip the player can fly.
    a_km: 20428,
    e: 0.0,
    i_deg: 25.19,
    long_node_deg: 49.56,
    arg_peri_deg: 0,
    mean_anomaly_deg: 118,
  },
  {
    id: 'phobos-low',
    label: 'low Phobos orbit',
    parent: 'phobos',
    // 25 km from the centre, clear of the long axis and close enough that the
    // moon is the body the sim shows. Nothing holds an orbit about Phobos -
    // its hold radius against Mars sits under its own surface - so this row is
    // a place to start from and not a path anything stays on.
    a_km: 25,
    e: 0.0,
    i_deg: 26.0,
    long_node_deg: 49.56,
    arg_peri_deg: 0,
    mean_anomaly_deg: 0,
  },
  {
    id: 'earth-high',
    label: 'high Earth orbit',
    parent: 'earth',
    // Geostationary: 42,164 km, six radii out, the same stand-off `mars-high`
    // gives at Mars and well inside Earth's own SYSTEM edge, so cruise draws
    // Earth as the body underfoot. `descent-dojo --underfoot` starts here.
    a_km: 42164,
    e: 0.0,
    i_deg: 23.44,
    long_node_deg: 0,
    arg_peri_deg: 0,
    mean_anomaly_deg: 0,
  },
  {
    id: 'pluto-high',
    label: 'high Pluto orbit',
    parent: 'pluto',
    a_km: 9000,
    e: 0.0,
    i_deg: 30.0,
    long_node_deg: 110.3,
    arg_peri_deg: 0,
    mean_anomaly_deg: 41,
  },
];

export function shipOrbitById(id: string): ShipOrbitSpec | undefined {
  return SHIP_ORBITS.find((spec) => spec.id === id);
}

/**
 * The element set `src/world/ephemeris.ts` propagates, built from the spec and
 * the parent's own mass. Two-body: the only rate is the mean longitude's, which
 * is the mean motion the parent's GM gives at this semi-major axis.
 */
export function shipOrbitElements(spec: ShipOrbitSpec, parent: BodyRow): Orbit {
  const aKm = spec.a_km;
  const gm = G_KM3_KG_S2 * parent.mass_kg;
  const nRadPerSec = Math.sqrt(gm / (aKm * aKm * aKm));
  const nDegPerDay = (nRadPerSec * SECONDS_PER_DAY * 180) / Math.PI;
  const longPeri = spec.arg_peri_deg + spec.long_node_deg;
  return {
    epoch: `ship_orbit:${spec.id}`,
    epoch_jd: J2000_JD,
    frame: 'ecliptic_j2000',
    parent_relative: true,
    a_au: aKm / AU_KM,
    e: spec.e,
    i_deg: spec.i_deg,
    L_deg: spec.mean_anomaly_deg + longPeri,
    long_peri_deg: longPeri,
    long_node_deg: spec.long_node_deg,
    rates: {
      a_au_cy: 0,
      e_cy: 0,
      i_deg_cy: 0,
      L_deg_cy: nDegPerDay * DAYS_PER_CENTURY,
      long_peri_deg_cy: 0,
      long_node_deg_cy: 0,
    },
    extra_terms: null,
    rates_source: 'two_body_mean_motion',
  };
}
